import { type FC, type SVGProps } from "react";
import { type Translation } from "../hooks/useTranslations";
import de from "../i18n/de";
import en from "../i18n/en";
import es from "../i18n/es";
import fr from "../i18n/fr";
import it from "../i18n/it";

export type Glyph = FC<SVGProps<SVGSVGElement>>;

export type FetcherOptions = {
  url: string;
  signal?: AbortSignal;
};

export interface FilterParam {
  fieldname: string;
  values?: Exclude<MenuOptionValue, Lookup>[];
  // operator?: "IS_NULL" | "IS_NOT_NULL";
}

export type ListFilters = {
  page: number;
  limit?: number;
  q?: string;
  orderBy?: string;
  desc: boolean;
  filters?: FilterParam[];
};

export type Paginated<P> = {
  items: P[];
  total: number;
};

export type PaginationQuery = Partial<ListFilters>;
export type PaginationQueryWithFields<T, F extends DotNestedKeys<T> = DotNestedKeys<T>> = PaginationQuery & {
  fields: ReadonlyArray<F>;
};

export type Lookup = {
  id: string;
  value: string;
};

export const isLookup = (value: MenuOptionValue): value is Lookup => {
  return !!value && !!(value as Lookup).id && !!(value as Lookup).value;
};

export type MenuOptionValue = Lookup | string | number | boolean | null;

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;
type Simplify<T> = { [K in keyof T]: T[K] } & {};
type IsPlainObject<T> = T extends Function ? false : T extends any[] ? false : T extends object ? true : false;
type IsOptionalProp<T, K extends keyof T> = {} extends Pick<T, K> ? true : false;
// Modified: arrays only generate paths with []
export type DotNestedKeys<T> = T extends object
  ? {
      [K in Extract<keyof T, string>]: NonNullable<T[K]> extends Array<infer U>
        ? `${K}[]` | `${K}[].${DotNestedKeys<U>}`
        : IsPlainObject<NonNullable<T[K]>> extends true
          ? K | `${K}.${DotNestedKeys<NonNullable<T[K]>>}`
          : K;
    }[Extract<keyof T, string>]
  : never;

type _NestedPickSingle<T, P extends string> = P extends `${infer Key}[].${infer Rest}`
  ? Key extends keyof T
    ? NonNullable<T[Key]> extends Array<infer U>
      ? Extract<Rest, DotNestedKeys<U>> extends never
        ? never
        : IsOptionalProp<T, Key> extends true
          ? { [K in Key]?: Array<NestedPick<U, Extract<Rest, DotNestedKeys<U>>>> }
          : { [K in Key]: Array<NestedPick<U, Extract<Rest, DotNestedKeys<U>>>> }
      : never
    : never
  : P extends `${infer Key}[]`
    ? Key extends keyof T
      ? NonNullable<T[Key]> extends Array<infer U>
        ? IsOptionalProp<T, Key> extends true
          ? { [K in Key]?: Array<U> }
          : { [K in Key]: Array<U> }
        : never
      : never
    : P extends `${infer Key}.${infer Rest}`
      ? Key extends keyof T
        ? IsPlainObject<NonNullable<T[Key]>> extends true
          ? Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>> extends never
            ? never
            : IsOptionalProp<T, Key> extends true
              ? { [K in Key]?: NestedPick<NonNullable<T[Key]>, Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>>> }
              : { [K in Key]: NestedPick<NonNullable<T[Key]>, Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>>> }
          : never
        : never
      : P extends keyof T
        ? NonNullable<T[P]> extends Array<any>
          ? never // Reject plain array keys without []
          : IsOptionalProp<T, P> extends true
            ? { [K in P]?: T[P] }
            : { [K in P]: T[P] }
        : never;
export type NestedPick<T, P extends DotNestedKeys<T>> = Simplify<
  UnionToIntersection<P extends any ? _NestedPickSingle<T, P> : never>
>;

export type LanguageCode =
  | UILanguageCode
  | "en"
  | "it"
  | "de"
  | "es"
  | "fr"
  | "ar"
  | "cs"
  | "da"
  | "el"
  | "et"
  | "fi"
  | "hr"
  | "hu"
  | "is"
  | "ja"
  | "lt"
  | "lv"
  | "nl"
  | "no"
  | "pl"
  | "pt"
  | "ru"
  | "sk"
  | "sl"
  | "sv"
  | "tr"
  | "zh"
  | "kr";

export type CATEGORY_CODE = "fastening-systems" | "fall-protection" | "waterproofing";

export type Language = {
  value: LanguageCode;
  display: string;
};

export const SupportedLanguages = ["en", "de", "es", "fr", "it"] as const;

export type UILanguageCode = (typeof SupportedLanguages)[number];

// export type UILanguageCode = "en" | "it" | "de" | "es" | "fr";

export type UITranslationLanguageCode = (typeof UI_TRANSLATION_LANGUAGE_CODES)[number];

export const UI_TRANSLATION_LANGUAGE_CODES = [
  it.languageCode,
  de.languageCode,
  es.languageCode,
  fr.languageCode,
] as const;

export type UILanguage = {
  value: UILanguageCode;
  display: string;
};

export const getUILanguages = (translations: Translation): Language[] => [
  { value: en.languageCode, display: translations.common.languages.english },
  { value: it.languageCode, display: translations.common.languages.italian },
  { value: de.languageCode, display: translations.common.languages.german },
  { value: es.languageCode, display: translations.common.languages.spanish },
  { value: fr.languageCode, display: translations.common.languages.french },
];

export const getUITranslationLanguages = (
  translations: Translation,
): { value: UITranslationLanguageCode; display: string }[] =>
  getUILanguages(translations)
    .filter((l) => l.value !== "en")
    .map((l) => ({ value: l.value as UITranslationLanguageCode, display: l.display }));

export const getAllLanguages = (translations: Translation): Language[] => {
  const rest: Language[] = [
    { value: "ar", display: translations.common.languages.arabic },
    { value: "cs", display: translations.common.languages.czech },
    { value: "da", display: translations.common.languages.danish },
    { value: "el", display: translations.common.languages.greek },
    { value: "et", display: translations.common.languages.estonian },
    { value: "fi", display: translations.common.languages.finnish },
    { value: "hr", display: translations.common.languages.croatian },
    { value: "hu", display: translations.common.languages.hungarian },
    { value: "is", display: translations.common.languages.icelandic },
    { value: "ja", display: translations.common.languages.japanese },
    { value: "lt", display: translations.common.languages.lithuanian },
    { value: "lv", display: translations.common.languages.latvian },
    { value: "nl", display: translations.common.languages.dutch },
    { value: "no", display: translations.common.languages.norwegian },
    { value: "pl", display: translations.common.languages.polish },
    { value: "pt", display: translations.common.languages.portugese },
    { value: "ru", display: translations.common.languages.russian },
    { value: "sk", display: translations.common.languages.slovak },
    { value: "sl", display: translations.common.languages.slovenian },
    { value: "sv", display: translations.common.languages.swedish },
    { value: "tr", display: translations.common.languages.turkish },
    { value: "zh", display: translations.common.languages.chinese },
    { value: "kr", display: translations.common.languages.korean },
  ];

  return [...getUILanguages(translations), ...rest.sort((a, b) => a.display.localeCompare(b.display))];
};

// Mirrors public.unit_system_types.
export const UNIT_SYSTEM_METRIC = "metric";
export const UNIT_SYSTEM_IMPERIAL = "imperial";
export type UnitSystem = typeof UNIT_SYSTEM_METRIC | typeof UNIT_SYSTEM_IMPERIAL;

export type UpdateOrderRequestBody = {
  order: number;
};

export type GenerateUrlRequestBody = {
  filename: string;
  contentType: string;
};

export type UploadFileResponse = {
  ok: boolean;
  status: number;
};

export type Text = {
  value: string;
  image?: string;
};

export type ItemFixingWithQuantity = {
  wallMaterial: {
    id: string;
    name: string;
  };
  quantity: number | null;
};

export type Path = {
  start: LatLng;
  end: LatLng;
};

export type LatLng = {
  lat: number;
  lng: number;
};

export type XOR<T, U> = (T & { [K in keyof U]?: never }) | (U & { [K in keyof T]?: never });

export const getAllTypes = (): { value: string; label: string }[] => {
  return [
    { value: "bom", label: "BoM" },
    { value: "structural-calculation", label: "Structural-Calculation" },
  ];
};
